import type { CatalogEntry, CatalogFile, CatalogImportResult, VoltageClass, CoolingMethod, RULParams } from '../types/catalog';
import type { Vendor, TripKey, TripCode } from '../types/drive';
import { parseCSVLines } from '../utils/csv';

const VALID_VENDORS: string[] = ['SIEMENS', 'DANFOSS', 'WEG', 'ABB', 'SCHNEIDER', 'ROCKWELL'];
const VALID_VOLTAGE_CLASSES: string[] = ['110V', '220V', '230V', '380V', '400V', '460V', '480V', '575V', '690V'];
const VALID_COOLING: string[] = ['forced-air', 'liquid', 'natural'];
const TRIP_KEYS: TripKey[] = ['OC', 'OV', 'UV', 'OT', 'FAN'];

interface ColumnMap {
  idx: number;
  field: string;
}

export class CatalogImporter {

  static importCSV(csvText: string, fileName: string): CatalogImportResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const lines = this.parseCSVLines(csvText);
    if (lines.length < 2) {
      return { success: false, errors: ['CSV vazio ou sem linhas de dados'], warnings: [] };
    }

    const header = lines[0];
    const columnMap = this.buildColumnMap(header, errors);
    if (errors.length > 0) {
      return { success: false, errors, warnings };
    }

    const models: CatalogEntry[] = [];
    const seenModelCodes = new Set<string>();
    for (let i = 1; i < lines.length; i++) {
      const result = this.parseRow(lines[i], columnMap, i + 1, warnings);
      if (result) {
        if (seenModelCodes.has(result.modelCode)) {
          warnings.push(`Linha ${i + 1}: modelo ${result.modelCode} duplicado no arquivo, ignorado`);
          continue;
        }
        seenModelCodes.add(result.modelCode);
        models.push(result);
      }
    }

    if (models.length === 0) {
      errors.push('Nenhum modelo válido encontrado no CSV');
      return { success: false, errors, warnings };
    }

    const vendor = models[0].vendor;
    const family = models[0].family;

    const catalogFile: CatalogFile = {
      catalogName: fileName.replace(/\.csv$/i, ''),
      vendor,
      family,
      importedAt: new Date().toISOString(),
      models,
    };

    return {
      success: true,
      catalogName: catalogFile.catalogName,
      modelCount: models.length,
      models,
      catalogFile,
      errors,
      warnings,
    };
  }

  static parseCSVLines(text: string): string[][] {
    return parseCSVLines(text);
  }

  private static buildColumnMap(header: string[], errors: string[]): ColumnMap[] {
    const requiredFields = [
      'modelCode', 'family', 'name', 'vendor', 'powerKW', 'nominalCurrent',
      'voltageClass', 'ratedFrequency', 'coolingMethod', 'protectionRating',
      'fanCount', 'maxTemp', 'nominalDC', 'fanMainLife', 'fanControlLife',
      'capacitorBaseLife', 'igbtMaxCycles', 'bearingBaseLife',
      'regFreq', 'regCurrent', 'regDC', 'regTemp', 'regWriteFreq',
      'poles', 'currentScale',
      'tripOC_code', 'tripOC_mask', 'tripOC_desc',
      'tripOV_code', 'tripOV_mask', 'tripOV_desc',
      'tripUV_code', 'tripUV_mask', 'tripUV_desc',
      'tripOT_code', 'tripOT_mask', 'tripOT_desc',
      'tripFAN_code', 'tripFAN_mask', 'tripFAN_desc',
    ];

    const map: ColumnMap[] = [];
    const missing: string[] = [];

    for (const field of requiredFields) {
      const idx = header.findIndex(h => h.trim().toLowerCase() === field.toLowerCase());
      if (idx === -1) {
        missing.push(field);
      } else {
        map.push({ idx, field });
      }
    }

    if (missing.length > 0) {
      errors.push(`Colunas obrigatórias ausentes: ${missing.join(', ')}`);
    }

    return map;
  }

  private static parseRow(
    cells: string[],
    columnMap: ColumnMap[],
    rowNum: number,
    warnings: string[],
  ): CatalogEntry | null {
    const get = (field: string): string => {
      const col = columnMap.find(c => c.field === field);
      if (!col || col.idx >= cells.length) return '';
      return cells[col.idx];
    };

    const getNum = (field: string, def: number = 0): number => {
      const val = get(field);
      if (val === '') return def;
      const num = parseFloat(val);
      return isNaN(num) ? def : num;
    };

    const modelCode = get('modelCode');
    if (!modelCode) {
      warnings.push(`Linha ${rowNum}: modelCode vazio, ignorada`);
      return null;
    }

    const vendor = get('vendor').toUpperCase() as Vendor;
    if (!VALID_VENDORS.includes(vendor)) {
      warnings.push(`Linha ${rowNum}: fabricante inválido "${vendor}" para modelo "${modelCode}", ignorada`);
      return null;
    }

    const voltageClass = get('voltageClass').toUpperCase() as VoltageClass;
    if (!VALID_VOLTAGE_CLASSES.includes(voltageClass)) {
      warnings.push(`Linha ${rowNum}: classe de tensão inválida "${voltageClass}" para modelo "${modelCode}", ignorada`);
      return null;
    }

    const coolingMethod = get('coolingMethod').toLowerCase() as CoolingMethod;
    if (!VALID_COOLING.includes(coolingMethod)) {
      warnings.push(`Linha ${rowNum}: método de refrigeração inválido "${coolingMethod}" para modelo "${modelCode}", ignorada`);
      return null;
    }

    const rulParams: RULParams = {
      fanMainLife: getNum('fanMainLife', 50000),
      fanControlLife: getNum('fanControlLife', 30000),
      capacitorBaseLife: getNum('capacitorBaseLife', 80000),
      igbtMaxCycles: getNum('igbtMaxCycles', 50000),
      bearingBaseLife: getNum('bearingBaseLife', 40000),
    };

    const modbusMap = {
      regFreq: getNum('regFreq'),
      regCurrent: getNum('regCurrent'),
      regDC: getNum('regDC'),
      regTemp: getNum('regTemp'),
      regWriteFreq: getNum('regWriteFreq'),
      poles: getNum('poles', 4),
      currentScale: getNum('currentScale', 10),
    };

    const tripCodes: Record<TripKey, TripCode> = {} as Record<TripKey, TripCode>;
    for (const tk of TRIP_KEYS) {
      tripCodes[tk] = {
        code: get(`trip${tk}_code`) || `F${tk}`,
        mask: getNum(`trip${tk}_mask`, 0),
        desc: get(`trip${tk}_desc`) || `Trip ${tk}`,
      };
    }

    return {
      modelCode,
      family: get('family') || 'General',
      name: get('name') || modelCode,
      vendor,
      powerKW: getNum('powerKW'),
      nominalCurrent: getNum('nominalCurrent'),
      voltageClass,
      ratedFrequency: getNum('ratedFrequency', 60),
      coolingMethod,
      protectionRating: get('protectionRating') || 'IP20',
      fanCount: getNum('fanCount', 1),
      maxTemp: getNum('maxTemp', 85),
      nominalDC: getNum('nominalDC', 560),
      rulParams,
      modbusMap,
      tripCodes,
    };
  }

  static importJSON(jsonText: string, fileName: string): CatalogImportResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    let parsed: Partial<CatalogFile>;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      errors.push('JSON inválido: erro de sintaxe');
      return { success: false, errors, warnings: [] };
    }

    if (!parsed.vendor || !parsed.models || !Array.isArray(parsed.models)) {
      errors.push('JSON inválido: campos obrigatórios ausentes (vendor, models)');
      return { success: false, errors, warnings: [] };
    }

    const models: CatalogEntry[] = [];
    const seenModelCodes = new Set<string>();
    for (let i = 0; i < parsed.models.length; i++) {
      const m = parsed.models[i];
      if (!m.modelCode) {
        warnings.push(`Modelo ${i + 1}: modelCode vazio, ignorado`);
        continue;
      }
      if (!VALID_VENDORS.includes(m.vendor?.toUpperCase())) {
        warnings.push(`Modelo ${i + 1} (${m.modelCode}): fabricante inválido "${m.vendor}", ignorado`);
        continue;
      }
      if (seenModelCodes.has(m.modelCode)) {
        warnings.push(`Modelo ${i + 1} (${m.modelCode}): código duplicado no arquivo, ignorado`);
        continue;
      }
      seenModelCodes.add(m.modelCode);
      const nd = m.nominalCurrentND ?? m.nominalCurrentND;
      const hd = m.nominalCurrentHD;

      models.push({
        modelCode: m.modelCode,
        family: m.family || '',
        name: m.name || m.modelCode,
        vendor: m.vendor?.toUpperCase() as Vendor,
        powerKW: m.powerKW ?? 0,
        nominalCurrent: m.nominalCurrent ?? nd ?? 0,
        nominalCurrentND: nd,
        nominalCurrentHD: hd,
        voltageClass: m.voltageClass || '380V',
        ratedFrequency: m.ratedFrequency ?? 60,
        coolingMethod: m.coolingMethod || 'forced-air',
        protectionRating: m.protectionRating || 'IP20',
        fanCount: m.fanCount ?? 1,
        maxTemp: m.maxTemp ?? 85,
        nominalDC: m.nominalDC ?? 560,
        partNumber: m.partNumber,
        orderingNumber: m.orderingNumber,
        frameSize: m.frameSize,
        powerCardCode: m.powerCardCode,
        igbtModuleCode: m.igbtModuleCode,
        fanCode: m.fanCode,
        capacitorBankCode: m.capacitorBankCode,
        rulParams: {
          fanMainLife: m.rulParams?.fanMainLife ?? 50000,
          fanControlLife: m.rulParams?.fanControlLife ?? 30000,
          capacitorBaseLife: m.rulParams?.capacitorBaseLife ?? 80000,
          igbtMaxCycles: m.rulParams?.igbtMaxCycles ?? 50000,
          bearingBaseLife: m.rulParams?.bearingBaseLife ?? 40000,
        },
        modbusMap: {
          regFreq: m.modbusMap?.regFreq ?? 40022,
          regCurrent: m.modbusMap?.regCurrent ?? 40025,
          regDC: m.modbusMap?.regDC ?? 40024,
          regTemp: m.modbusMap?.regTemp ?? 40029,
          regWriteFreq: m.modbusMap?.regWriteFreq ?? 40101,
          poles: m.modbusMap?.poles ?? 4,
          currentScale: m.modbusMap?.currentScale ?? 10,
        },
        tripCodes: m.tripCodes || {
          OC: { code: 'F001', mask: 0x0001, desc: 'Overcurrent' },
          OV: { code: 'F002', mask: 0x0002, desc: 'Overvoltage' },
          UV: { code: 'F003', mask: 0x0004, desc: 'Undervoltage' },
          OT: { code: 'F004', mask: 0x0008, desc: 'Overtemp' },
          FAN: { code: 'F005', mask: 0x0010, desc: 'Fan Fault' },
        },
      });
    }

    if (models.length === 0) {
      errors.push('Nenhum modelo válido encontrado no JSON');
      return { success: false, errors, warnings };
    }

    const catalogFile: CatalogFile = {
      catalogName: fileName.replace(/\.json$/i, ''),
      vendor: parsed.vendor,
      family: parsed.family || models[0].family,
      importedAt: new Date().toISOString(),
      models,
    };

    return {
      success: true,
      catalogName: catalogFile.catalogName,
      modelCount: models.length,
      models,
      catalogFile,
      errors,
      warnings,
    };
  }

  static exportCatalogToJSON(catalog: CatalogFile): string {
    return JSON.stringify(catalog, null, 2);
  }

  static catalogToJSON(catalog: CatalogFile): string {
    return JSON.stringify(catalog, null, 2);
  }
}
